import React from 'react';
import {
  COLORS,
  SPACING,
  FONT_SIZE,
  FONT_WEIGHT,
  BORDER_RADIUS,
  SHADOWS,
  TOUCH_TARGET,
  TRANSITIONS,
} from '../../constants/theme';

/**
 * PlayerCard Component
 * Mobile-optimized card for displaying player information with drill progress
 *
 * @param {Object} player - The player object with drillStats
 * @param {Function} onAssignDrills - Callback when "Assign Drills" is clicked
 * @param {Function} onRecordStats - Callback when "Record Stats" is clicked
 */
const PlayerCard = ({ player, onAssignDrills, onRecordStats }) => {
  const drillStats = player.drillStats || { total: 0, completed: 0 };
  const progressPercentage =
    drillStats.total > 0 ? (drillStats.completed / drillStats.total) * 100 : 0;

  // Determine progress bar color based on completion
  const getProgressColor = () => {
    if (progressPercentage === 100) return COLORS.success;
    if (progressPercentage >= 50) return COLORS.primary;
    if (progressPercentage > 0) return COLORS.warning;
    return COLORS.danger;
  };

  const styles = {
    card: {
      backgroundColor: COLORS.white,
      borderRadius: BORDER_RADIUS.md,
      boxShadow: SHADOWS.md,
      padding: SPACING.lg,
      marginBottom: SPACING.md,
      border: `1px solid ${COLORS.gray[300]}`,
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: SPACING.md,
      paddingBottom: SPACING.md,
      borderBottom: `1px solid ${COLORS.gray[200]}`,
    },
    title: {
      fontSize: FONT_SIZE.lg,
      fontWeight: FONT_WEIGHT.semibold,
      color: COLORS.gray[700],
    },
    body: {
      display: 'flex',
      flexDirection: 'column',
      gap: SPACING.sm,
      marginBottom: SPACING.md,
      fontSize: FONT_SIZE.base,
    },
    label: {
      fontWeight: FONT_WEIGHT.semibold,
      color: COLORS.gray[600],
      fontSize: FONT_SIZE.md,
    },
    value: {
      color: COLORS.gray[700],
      fontSize: FONT_SIZE.md,
    },
    progressContainer: {
      marginTop: SPACING.md,
      marginBottom: SPACING.md,
    },
    progressLabel: {
      fontSize: FONT_SIZE.md,
      fontWeight: FONT_WEIGHT.semibold,
      color: COLORS.gray[600],
      marginBottom: '6px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    progressStats: {
      fontWeight: FONT_WEIGHT.bold,
      color: COLORS.gray[700],
    },
    progressBarBackground: {
      width: '100%',
      height: '8px',
      backgroundColor: COLORS.gray[300],
      borderRadius: BORDER_RADIUS.sm,
      overflow: 'hidden',
    },
    progressBarFill: {
      height: '100%',
      borderRadius: BORDER_RADIUS.sm,
      transition: TRANSITIONS.slow,
      width: `${progressPercentage}%`,
      backgroundColor: getProgressColor(),
    },
    actions: {
      display: 'flex',
      flexDirection: 'column',
      gap: SPACING.sm,
    },
    button: {
      width: '100%',
      padding: SPACING.md,
      fontSize: FONT_SIZE.base,
      fontWeight: FONT_WEIGHT.medium,
      border: 'none',
      borderRadius: BORDER_RADIUS.sm,
      cursor: 'pointer',
      textAlign: 'center',
      minHeight: TOUCH_TARGET.minimum,
    },
    primaryButton: {
      backgroundColor: COLORS.primary,
      color: COLORS.white,
    },
    successButton: {
      backgroundColor: COLORS.success,
      color: COLORS.white,
    },
  };

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>{player.full_name}</div>
      </div>

      <div style={styles.body}>
        <div>
          <div style={styles.label}>Email</div>
          <div style={styles.value}>{player.email}</div>
        </div>
        {player.phone && (
          <div>
            <div style={styles.label}>Phone</div>
            <div style={styles.value}>{player.phone}</div>
          </div>
        )}
        {player.tags && player.tags.length > 0 && (
          <div>
            <div style={styles.label}>Tags</div>
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '4px' }}>
              {player.tags.map((tag) => (
                <span
                  key={tag.id}
                  style={{
                    padding: '2px 8px',
                    backgroundColor: tag.color,
                    color: 'white',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500',
                  }}
                >
                  {tag.name}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div style={styles.progressContainer}>
        <div style={styles.progressLabel}>
          <span>Drill Progress</span>
          <span style={styles.progressStats}>
            {drillStats.completed}/{drillStats.total} completed
          </span>
        </div>
        <div style={styles.progressBarBackground}>
          <div style={styles.progressBarFill} />
        </div>
      </div>

      <div style={styles.actions}>
        <button
          style={{ ...styles.button, ...styles.primaryButton }}
          onClick={() => onAssignDrills(player)}
        >
          Assign Drills
        </button>
        <button
          style={{ ...styles.button, ...styles.successButton }}
          onClick={() => onRecordStats(player)}
        >
          Record Stats
        </button>
      </div>
    </div>
  );
};

export default PlayerCard;
